import React, { useState, useEffect } from 'react';
import { Card, Table, Form, Alert, Spinner, Row, Col, Badge } from 'react-bootstrap';
import AdminToolsLayout from './AdminToolsLayout';

/**
 * Finances Report Component
 * Shows per-model, per-tenant, and per-article financial breakdown
 * Includes guardrail status indicators
 */
const FinancesReport = () => {
  const [days, setDays] = useState(30);
  const [financesData, setFinancesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchFinancesData();
  }, [days]);

  const fetchFinancesData = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`/api/admin/tools/finances?days=${days}`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.error) {
        setError(data.error);
      } else {
        setFinancesData(data);
      }
    } catch (err) {
      setError('Failed to fetch finances data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getGuardrailBadge = (status) => {
    switch (status) {
      case '✅':
        return <Badge bg="success">OK</Badge>;
      case 'warn':
        return <Badge bg="warning">Under 200%</Badge>;
      case 'under-100':
        return <Badge bg="danger">Under 100%</Badge>;
      case 'missing-rate':
        return <Badge bg="danger">Missing Rate</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const formatCurrency = (value) => {
    return parseFloat(value || 0).toFixed(4);
  };

  const formatMargin = (margin) => {
    const m = parseFloat(margin || 0);
    return `${(m * 100).toFixed(0)}%`;
  };

  return (
    <AdminToolsLayout>
      <Card>
        <Card.Header>
          <h5 className="mb-0">Finances Report</h5>
        </Card.Header>
        <Card.Body>
          {/* Filters */}
          <Row className="mb-4">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Time Period</Form.Label>
                <Form.Select
                  value={days}
                  onChange={(e) => setDays(parseInt(e.target.value))}
                >
                  <option value="7">Last 7 days</option>
                  <option value="14">Last 14 days</option>
                  <option value="30">Last 30 days</option>
                  <option value="60">Last 60 days</option>
                  <option value="90">Last 90 days</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {error && <Alert variant="danger">{error}</Alert>}

          {loading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : !financesData ? (
            <p className="text-muted">No data available.</p>
          ) : (
            <>
              {/* Per-Model Table */}
              <section className="mb-5">
                <h6 className="mb-3">Per-Model Breakdown</h6>
                {financesData.perModel && financesData.perModel.length > 0 ? (
                  <Table striped bordered hover responsive size="sm">
                    <thead>
                      <tr>
                        <th>Provider</th>
                        <th>Model</th>
                        <th>Catalog Input Rate</th>
                        <th>Catalog Output Rate</th>
                        <th>Tokens Charged</th>
                        <th>Real Cost</th>
                        <th>Revenue</th>
                        <th>Effective Margin</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {financesData.perModel.map((model, idx) => (
                        <tr key={idx}>
                          <td>{model.provider}</td>
                          <td>{model.modelUsed}</td>
                          <td>${formatCurrency(model.catalogInputRate)}</td>
                          <td>${formatCurrency(model.catalogOutputRate)}</td>
                          <td>{parseInt(model.tokensCharged).toLocaleString()}</td>
                          <td>${formatCurrency(model.realCost)}</td>
                          <td>${formatCurrency(model.revenue)}</td>
                          <td>{formatMargin(model.effectiveMargin)}</td>
                          <td>
                            {model.guardrailStatus === 'missing-rate' && (
                              <Badge bg="danger">Missing Rate</Badge>
                            )}
                            {model.guardrailStatus === 'under-100' && (
                              <Badge bg="danger">Under 100%</Badge>
                            )}
                            {model.guardrailStatus === 'warn' && (
                              <Badge bg="warning">Under 200%</Badge>
                            )}
                            {model.guardrailStatus === 'ok' && (
                              <Badge bg="success">OK 3x</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <p className="text-muted">No model data available.</p>
                )}
              </section>

              {/* Per-Tenant Table */}
              <section className="mb-5">
                <h6 className="mb-3">Per-Tenant Breakdown</h6>
                {financesData.perTenant && financesData.perTenant.length > 0 ? (
                  <Table striped bordered hover responsive size="sm">
                    <thead>
                      <tr>
                        <th>Tenant Slug</th>
                        <th>Tier</th>
                        <th>Markup %</th>
                        <th>Tokens Consumed</th>
                        <th>Real Cost</th>
                        <th>Revenue</th>
                        <th>Effective Margin</th>
                        <th>Articles</th>
                        <th>Attribution (Sample)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {financesData.perTenant.map((tenant, idx) => (
                        <tr key={idx}>
                          <td>{tenant.tenantSlug}</td>
                          <td>{tenant.tier}</td>
                          <td>{formatMargin(tenant.markupPercentage)}</td>
                          <td>{parseInt(tenant.tokensConsumed).toLocaleString()}</td>
                          <td>${formatCurrency(tenant.realCost)}</td>
                          <td>${formatCurrency(tenant.revenue)}</td>
                          <td>{formatMargin(tenant.effectiveMargin)}</td>
                          <td>{tenant.articleCount}</td>
                          <td>
                            {tenant.attribution && tenant.attribution.length > 0 ? (
                              <small>{tenant.attribution.slice(0, 2).join(', ')}
                                {tenant.attribution.length > 2 && ` +${tenant.attribution.length - 2}`}
                              </small>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <p className="text-muted">No tenant data available.</p>
                )}
              </section>

              {/* Per-Article Table */}
              <section>
                <h6 className="mb-3">Recent Articles (Last 50)</h6>
                {financesData.perArticle && financesData.perArticle.length > 0 ? (
                  <Table striped bordered hover responsive size="sm">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Slug</th>
                        <th>Total Real Cost</th>
                        <th>Total Tokens Charged</th>
                        <th>Tenant / Email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {financesData.perArticle.map((article, idx) => (
                        <tr key={idx}>
                          <td style={{ maxWidth: '200px', wordBreak: 'break-word' }}>
                            {article.title}
                          </td>
                          <td>
                            <code style={{ fontSize: '0.85rem' }}>{article.slug}</code>
                          </td>
                          <td>${formatCurrency(article.totalRealCost)}</td>
                          <td>{parseInt(article.totalTokensCharged).toLocaleString()}</td>
                          <td><small>{article.tenantOrEmail}</small></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <p className="text-muted">No article data available.</p>
                )}
              </section>

              {/* Summary */}
              {financesData.dateRange && (
                <Card className="mt-4 bg-light">
                  <Card.Body>
                    <small className="text-muted">
                      Data range: {financesData.dateRange.startDate} to {financesData.dateRange.endDate}
                      ({financesData.dateRange.days} days)
                    </small>
                  </Card.Body>
                </Card>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </AdminToolsLayout>
  );
};

export default FinancesReport;
